import { Check, ChevronDown } from 'lucide-react';
import { Children, forwardRef, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type SelectHTMLAttributes } from 'react';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useNativeControl } from './native';
import { Popover, PopoverPanel, PopoverTrigger } from './popover';
import { controlClass, OPTION_ACTIVE, OPTION_DISABLED, OPTION_ROW } from './styles';

interface OptionItem {
  value: string;
  label: string;
  disabled: boolean;
}

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return '';
}

function collectOptions(children: ReactNode, out: OptionItem[] = []): OptionItem[] {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'option') {
      const props = child.props as { value?: string | number | readonly string[]; children?: ReactNode; disabled?: boolean; label?: string };
      const label = props.label ?? textOf(props.children);
      out.push({ value: props.value === undefined ? label : String(props.value), label, disabled: Boolean(props.disabled) });
      return;
    }
    const nested = (child.props as { children?: ReactNode }).children;
    if (nested !== undefined) collectOptions(nested, out);
  });
  return out;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  /** Shown when nothing is chosen yet. */
  placeholder?: string;
}

/**
 * A select with Bulk's own list instead of the operating system's.
 *
 * The real `<select>` stays in the DOM and keeps the value, so `{...register()}`,
 * `onChange(e.target.value)` and `<option>` children all work exactly as they did
 * with a plain element — only the open list is ours.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ className, invalid, placeholder, children, disabled, id, title, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, ...props }, ref) {
  const t = useT();
  const options = useMemo(() => collectOptions(children), [children]);
  const { ref: nativeRef, value, commit } = useNativeControl<HTMLSelectElement>(ref);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const optionId = (index: number) => `${listId}-${index}`;
  const typed = useRef({ query: '', at: 0 });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const step = useCallback(
    (from: number, delta: number) => {
      for (let i = from + delta; i >= 0 && i < options.length; i += delta) {
        if (!options[i]!.disabled) return i;
      }
      return from;
    },
    [options],
  );

  const onOpen = useRef({ selectedIndex, options, step });
  onOpen.current = { selectedIndex, options, step };

  /* Only when the list opens: a page that refreshes behind an open list must not
     drag the highlight back off whatever the arrow keys had reached. */
  useEffect(() => {
    if (!open) return;
    const { selectedIndex: index, options: items, step: nextEnabled } = onOpen.current;
    setActive(index >= 0 && !items[index]!.disabled ? index : nextEnabled(-1, 1));
  }, [open]);

  useEffect(() => {
    if (!open || active < 0) return;
    document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  function choose(index: number) {
    const option = options[index];
    if (!option || option.disabled) return;
    commit(option.value);
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => step(i, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => step(i, -1));
        return;
      case 'Home':
        event.preventDefault();
        setActive(step(-1, 1));
        return;
      case 'End':
        event.preventDefault();
        setActive(step(options.length, -1));
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(active);
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
        break;
    }
    if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return;
    const now = Date.now();
    typed.current = { query: (now - typed.current.at < 600 ? typed.current.query : '') + event.key.toLowerCase(), at: now };
    const hit = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(typed.current.query));
    if (hit >= 0) setActive(hit);
  }

  return (
    <div className={cn('relative w-full min-w-0', className)}>
      <select ref={nativeRef} disabled={disabled} tabIndex={-1} aria-hidden className="sr-only" {...props}>
        {children}
      </select>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            title={title}
            role="combobox"
            aria-controls={listId}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-invalid={invalid || undefined}
            aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            className={cn(controlClass({ invalid }), 'flex cursor-pointer items-center justify-between gap-2 pl-3 pr-2.5 text-left', open && 'border-slate-400 ring-2 ring-slate-200')}
          >
            <span className={cn('truncate', selected ? 'text-ink' : 'text-slate-400')}>{selected ? selected.label : (placeholder ?? '')}</span>
            <ChevronDown className={cn('h-4 w-4 shrink-0 text-ink-muted transition-transform duration-150', open && 'rotate-180')} aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverPanel className="max-h-(--radix-popover-content-available-height) w-max min-w-(--radix-popover-trigger-width) max-w-(--radix-popover-content-available-width) overflow-y-auto p-1" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div id={listId} role="listbox" className="scrollbar-thin max-h-72 overflow-y-auto">
            {options.length === 0 ? (
              <p className="px-2.5 py-2 text-sm text-ink-muted">{t('control.noOptions')}</p>
            ) : (
              options.map((option, index) => (
                <div
                  key={`${option.value}-${index}`}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === selectedIndex}
                  aria-disabled={option.disabled || undefined}
                  onPointerMove={() => !option.disabled && setActive(index)}
                  onClick={() => choose(index)}
                  className={cn(OPTION_ROW, option.disabled ? OPTION_DISABLED : index === active && OPTION_ACTIVE, index === selectedIndex && 'font-semibold')}
                >
                  <span className="min-w-0 flex-1 break-words">{option.label}</span>
                  {index === selectedIndex ? <Check className="h-4 w-4 shrink-0 text-ink" aria-hidden /> : null}
                </div>
              ))
            )}
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
});
